import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

// ─── Navigation Structure ────────────────────────────────────────────────────

const NAV_SECTIONS = [
  {
    title: "Getting Started",
    items: [
      { id: "welcome", label: "Welcome", icon: "\u{1F44B}" },
      { id: "create-account", label: "Create Your Account", icon: "\u{1F4DD}" },
      { id: "your-dashboard", label: "Your Dashboard", icon: "\u{1F4CA}" },
    ],
  },
  {
    title: "Set Up Your Chatbot",
    items: [
      { id: "branding", label: "Branding & Persona", icon: "\u{1F3A8}" },
      { id: "knowledge-base", label: "Knowledge Base (FAQs)", icon: "\u{1F4DA}" },
      { id: "embed-widget", label: "Embed on Your Website", icon: "\u{1F310}" },
      { id: "test-chatbot", label: "Test Your Chatbot", icon: "\u{1F9EA}" },
    ],
  },
  {
    title: "Tool Integrations",
    items: [
      { id: "tools-intro", label: "What Are Tools?", icon: "\u{1F527}" },
      { id: "tools-setup", label: "Setting Up a Tool", icon: "\u2795" },
      { id: "tools-examples", label: "Common Examples", icon: "\u{1F4A1}" },
      { id: "tools-testing", label: "Testing Your Tools", icon: "\u2705" },
    ],
  },
  {
    title: "Live Agent Support",
    items: [
      { id: "agents-overview", label: "How Live Chat Works", icon: "\u{1F91D}" },
      { id: "agents-manage", label: "Managing Your Team", icon: "\u{1F465}" },
      { id: "agents-workspace", label: "Agent Workspace", icon: "\u{1F4BB}" },
      { id: "agent-notifications", label: "Notifications & Alerts", icon: "\u{1F514}" },
      { id: "agent-widget", label: "Embeddable Agent Widget", icon: "\u{1F4E6}" },
      { id: "business-hours", label: "Business Hours", icon: "\u{1F552}" },
      { id: "canned-responses", label: "Canned Responses", icon: "\u{1F4AC}" },
    ],
  },
  {
    title: "Account",
    items: [
      { id: "plans", label: "Plans & Billing", icon: "\u{1F4B3}" },
      { id: "api-credentials", label: "API Credentials", icon: "\u{1F511}" },
      { id: "troubleshooting", label: "Troubleshooting", icon: "\u{1F6E0}\uFE0F" },
    ],
  },
];

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function Sidebar({ activeSection, onClose }: { activeSection: string; onClose: () => void }) {
  return (
    <nav className="docs-sidebar" id="sidebar">
      <div className="sidebar-logo">
        <h1>Onyx AI</h1>
        <span>User Guide</span>
      </div>
      {NAV_SECTIONS.map((section) => (
        <div className="sidebar-section" key={section.title}>
          <div className="sidebar-section-title">{section.title}</div>
          {section.items.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={`sidebar-link ${activeSection === item.id ? "active" : ""}`}
              onClick={onClose}
            >
              <span className="sidebar-link-icon">{item.icon}</span>
              {item.label}
            </a>
          ))}
        </div>
      ))}
    </nav>
  );
}

// ─── Reusable Components ─────────────────────────────────────────────────────

function Code({ children, label }: { children: string; label?: string }) {
  return (
    <div className="code-block">
      {label && <span className="code-label">{label}</span>}
      <pre><code>{children}</code></pre>
    </div>
  );
}

function InfoBox({ type, title, children }: { type: "tip" | "warning" | "note" | "danger"; title: string; children: React.ReactNode }) {
  const icons: Record<string, string> = { tip: "\u{1F4A1}", warning: "\u26A0\uFE0F", note: "\u{1F4DD}", danger: "\u{1F6A8}" };
  return (
    <div className={`info-box ${type}`}>
      <span className="info-box-icon">{icons[type]}</span>
      <div className="info-box-content">
        <strong>{title}</strong>
        {children}
      </div>
    </div>
  );
}

function StepList({ children }: { children: React.ReactNode }) {
  return <ol className="steps">{children}</ol>;
}

function ScreenHint({ title, description }: { title: string; description: string }) {
  return (
    <div className="screen-hint">
      <div className="screen-hint-header">
        <span className="screen-hint-dot red" />
        <span className="screen-hint-dot yellow" />
        <span className="screen-hint-dot green" />
        <span className="screen-hint-title">{title}</span>
      </div>
      <div className="screen-hint-body">{description}</div>
    </div>
  );
}

// ─── Getting Started ─────────────────────────────────────────────────────────

function WelcomeSection() {
  return (
    <section className="doc-section" id="welcome">
      <div className="docs-hero">
        <h1>Welcome to Onyx AI</h1>
        <p>
          Your intelligent customer support chatbot that combines AI-powered conversations
          with live human agent support. This guide will walk you through everything you need
          to get up and running.
        </p>
        <div className="hero-badges">
          <span className="hero-badge">AI-Powered Chat</span>
          <span className="hero-badge">Live Agent Support</span>
          <span className="hero-badge">API Integrations</span>
          <span className="hero-badge">Custom Branding</span>
        </div>
      </div>

      <h3>What Can Onyx AI Do For You?</h3>
      <div className="feature-grid">
        <div className="feature-card">
          <div className="feature-icon">{"\u{1F916}"}</div>
          <h4>AI Assistant</h4>
          <p>Your chatbot answers customer questions 24/7 using your FAQ knowledge base and custom instructions.</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon">{"\u{1F527}"}</div>
          <h4>API Integrations</h4>
          <p>Connect your existing systems so the AI can look up orders, check account balances, and more in real-time.</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon">{"\u{1F91D}"}</div>
          <h4>Live Agent Handoff</h4>
          <p>When the AI can't help, customers are seamlessly transferred to your human support team.</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon">{"\u{1F3A8}"}</div>
          <h4>Your Brand</h4>
          <p>Customize colors, logo, AI persona name, and welcome message to match your brand identity.</p>
        </div>
      </div>

      <h3>How It Works</h3>
      <div className="flow-visual">
        <div className="flow-step">
          <div className="flow-number">1</div>
          <div className="flow-label">Customer asks a question on your website</div>
        </div>
        <div className="flow-arrow">{"\u2192"}</div>
        <div className="flow-step">
          <div className="flow-number">2</div>
          <div className="flow-label">AI answers using your FAQs & connected tools</div>
        </div>
        <div className="flow-arrow">{"\u2192"}</div>
        <div className="flow-step">
          <div className="flow-number">3</div>
          <div className="flow-label">If needed, customer is transferred to a live agent</div>
        </div>
      </div>
    </section>
  );
}

function CreateAccountSection() {
  return (
    <section className="doc-section" id="create-account">
      <h2>Create Your Account</h2>
      <p>Getting started takes less than 2 minutes.</p>

      <StepList>
        <li>
          <strong>Go to the Portal</strong>
          <p>Navigate to your Onyx AI portal URL and click <strong>"Create Account"</strong>.</p>
        </li>
        <li>
          <strong>Enter your details</strong>
          <p>Provide your name, email, company name, and a secure password.</p>
        </li>
        <li>
          <strong>You're in!</strong>
          <p>After registering, you'll be taken to your dashboard. Your chatbot is already active with default settings &mdash; you can start customizing right away.</p>
        </li>
      </StepList>

      <InfoBox type="tip" title="Your API key is auto-generated">
        <p>An API key is created automatically when you register. You'll find it under <strong>Credentials</strong> in the sidebar. You'll need this to embed the chatbot on your site.</p>
      </InfoBox>
    </section>
  );
}

function DashboardSection() {
  return (
    <section className="doc-section" id="your-dashboard">
      <h2>Your Dashboard</h2>
      <p>The dashboard gives you a bird's-eye view of your chatbot's performance.</p>

      <ScreenHint
        title="Portal \u2192 Dashboard"
        description="Your main command center showing conversations, agent activity, and usage at a glance."
      />

      <h3>What You'll See</h3>
      <ul>
        <li><strong>Total Conversations</strong> &mdash; How many chats have happened this period</li>
        <li><strong>Active Sessions</strong> &mdash; Currently open conversations</li>
        <li><strong>Average Rating</strong> &mdash; Customer satisfaction score (1-5 stars)</li>
        <li><strong>Agent Status</strong> &mdash; Which agents are online, busy, or offline</li>
        <li><strong>Queue Size</strong> &mdash; How many customers are waiting for a live agent</li>
        <li><strong>Messages Used</strong> &mdash; Usage against your plan's monthly limit</li>
      </ul>

      <h3>Portal Navigation</h3>
      <p>Use the sidebar to access all sections:</p>
      <div className="nav-overview">
        <div className="nav-item"><strong>Dashboard</strong> &mdash; Performance overview</div>
        <div className="nav-item"><strong>Conversations</strong> &mdash; Browse and search all chats</div>
        <div className="nav-item"><strong>FAQs</strong> &mdash; Manage your knowledge base</div>
        <div className="nav-item"><strong>Tools</strong> &mdash; Set up API integrations</div>
        <div className="nav-item"><strong>Agents</strong> &mdash; Manage your support team</div>
        <div className="nav-item"><strong>Business Hours</strong> &mdash; Set availability schedule</div>
        <div className="nav-item"><strong>Canned Responses</strong> &mdash; Pre-written agent replies</div>
        <div className="nav-item"><strong>Analytics</strong> &mdash; Detailed performance metrics</div>
        <div className="nav-item"><strong>Settings</strong> &mdash; Branding, persona, system prompt</div>
        <div className="nav-item"><strong>Credentials</strong> &mdash; Your API key and embed code</div>
        <div className="nav-item"><strong>Billing</strong> &mdash; Plan and usage management</div>
      </div>
    </section>
  );
}

// ─── Chatbot Setup ───────────────────────────────────────────────────────────

function BrandingSection() {
  return (
    <section className="doc-section" id="branding">
      <h2>Branding & Persona</h2>
      <p>Make the chatbot feel like a natural part of your brand.</p>

      <ScreenHint
        title="Portal \u2192 Settings"
        description="Customize your chatbot's appearance, personality, and behavior."
      />

      <h3>What You Can Customize</h3>
      <table className="param-table">
        <thead>
          <tr><th>Setting</th><th>What It Does</th><th>Example</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Company Name</td>
            <td>Shown in the chat header</td>
            <td>"Acme Store"</td>
          </tr>
          <tr>
            <td>AI Persona Name</td>
            <td>The name your AI introduces itself as</td>
            <td>"Aria" (default), "Max", "Luna"</td>
          </tr>
          <tr>
            <td>Brand Color</td>
            <td>Primary color for the chat widget</td>
            <td>#6C5CE7, #00B894</td>
          </tr>
          <tr>
            <td>Logo URL</td>
            <td>Your company logo in the chat header</td>
            <td>https://yoursite.com/logo.png</td>
          </tr>
          <tr>
            <td>Welcome Message</td>
            <td>First message shown when a customer opens the chat</td>
            <td>"Hi! How can I help you today?"</td>
          </tr>
          <tr>
            <td>System Prompt</td>
            <td>Custom instructions that guide the AI's behavior and tone</td>
            <td>"You are a friendly support agent for a pet supply store. Always suggest related products."</td>
          </tr>
        </tbody>
      </table>

      <InfoBox type="tip" title="Writing a Good System Prompt">
        <p>Your system prompt shapes how the AI behaves. Here are some tips:</p>
        <ul>
          <li>Describe your business and what you sell/offer</li>
          <li>Set the tone (formal, casual, friendly)</li>
          <li>Tell the AI what to do when it doesn't know the answer</li>
          <li>Mention any policies (refunds, shipping, etc.)</li>
        </ul>
      </InfoBox>

      <h3>Example System Prompt</h3>
      <Code>{`You are a helpful customer support assistant for TechGadgets, an online electronics store.

Be friendly but professional. Use the customer's name when available.

Key policies:
- Free shipping on orders over $50
- 30-day return policy for unused items
- Refunds processed within 5-7 business days

If you don't know the answer, offer to connect the customer with a live agent.
Never make up order statuses — use the lookup_order tool if available.`}</Code>
    </section>
  );
}

function KnowledgeBaseSection() {
  return (
    <section className="doc-section" id="knowledge-base">
      <h2>Knowledge Base (FAQs)</h2>
      <p>
        FAQs are the foundation of your chatbot's knowledge. When a customer asks a question,
        the AI searches your FAQ database and uses matching entries to craft accurate answers.
      </p>

      <ScreenHint
        title="Portal \u2192 FAQs"
        description="Add, edit, and organize your frequently asked questions."
      />

      <h3>Adding FAQs</h3>
      <StepList>
        <li>
          <strong>Click "Add FAQ"</strong>
          <p>Open the FAQ manager from the sidebar.</p>
        </li>
        <li>
          <strong>Write the question</strong>
          <p>Phrase it the way your customers would ask. For example: "What is your return policy?"</p>
        </li>
        <li>
          <strong>Write the answer</strong>
          <p>Provide a clear, complete answer. The AI will use this to respond naturally &mdash; it won't copy-paste it word for word.</p>
        </li>
        <li>
          <strong>Set a category (optional)</strong>
          <p>Group related FAQs (e.g. "Shipping", "Returns", "Account") to keep things organized.</p>
        </li>
      </StepList>

      <InfoBox type="tip" title="Quality Over Quantity">
        <p>Well-written FAQs make a bigger difference than having lots of them. Focus on:</p>
        <ul>
          <li>Your most common customer questions</li>
          <li>Clear, detailed answers with specific numbers and policies</li>
          <li>Covering different phrasings in the question field</li>
        </ul>
      </InfoBox>

      <h3>Example FAQs</h3>
      <div className="faq-example">
        <div className="faq-q">Q: How long does shipping take?</div>
        <div className="faq-a">A: Standard shipping takes 5-7 business days. Express shipping (available for $12.99) takes 2-3 business days. Free shipping is available on orders over $50.</div>
      </div>
      <div className="faq-example">
        <div className="faq-q">Q: How do I reset my password?</div>
        <div className="faq-a">A: Click "Forgot Password" on the login page, enter your email address, and we'll send you a reset link. The link expires after 24 hours. If you don't receive the email, check your spam folder.</div>
      </div>
    </section>
  );
}

function EmbedSection() {
  return (
    <section className="doc-section" id="embed-widget">
      <h2>Embed on Your Website</h2>
      <p>
        Add the Onyx AI chat widget to your website with a single code snippet.
      </p>

      <ScreenHint
        title="Portal \u2192 Credentials"
        description="Find your API key and ready-to-use embed code."
      />

      <h3>Get Your Embed Code</h3>
      <StepList>
        <li>
          <strong>Go to Credentials</strong>
          <p>In the portal sidebar, click <strong>Credentials</strong>.</p>
        </li>
        <li>
          <strong>Copy your API key</strong>
          <p>You'll see your unique API key. Click to copy it.</p>
        </li>
        <li>
          <strong>Add the script to your website</strong>
          <p>Paste the following snippet before the closing <code>&lt;/body&gt;</code> tag on every page where you want the chatbot:</p>
        </li>
      </StepList>

      <Code label="html">{`<script>
  window.OnyxAI = {
    apiKey: "YOUR_API_KEY",
    // Optional settings:
    // userId: "customer-123",
    // userName: "Jane Smith",
    // userType: "customer",
    // userMeta: { plan: "pro", region: "US" },
  };
</script>
<script src="https://your-onyx-domain.com/widget.js" async></script>`}</Code>

      <h3>Configuration Options</h3>
      <table className="param-table">
        <thead>
          <tr><th>Option</th><th>Required?</th><th>Description</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>apiKey</td>
            <td>Yes</td>
            <td>Your unique API key from the Credentials page</td>
          </tr>
          <tr>
            <td>userId</td>
            <td>No</td>
            <td>Your internal customer ID. Helps track conversations per customer.</td>
          </tr>
          <tr>
            <td>userName</td>
            <td>No</td>
            <td>Customer's name. The AI will greet them personally.</td>
          </tr>
          <tr>
            <td>userType</td>
            <td>No</td>
            <td>"customer" or "dealer" &mdash; gives the AI extra context</td>
          </tr>
          <tr>
            <td>userMeta</td>
            <td>No</td>
            <td>Any extra info about the user (plan level, region, etc.)</td>
          </tr>
        </tbody>
      </table>

      <InfoBox type="note" title="Passing User Context">
        <p>If you pass <code>userId</code> and <code>userName</code>, the AI can personalize its responses. For example, if a logged-in customer opens the chat, the AI might say "Hi Jane! How can I help you today?"</p>
      </InfoBox>

      <h3>Direct API Integration</h3>
      <p>
        If you prefer to build your own chat UI instead of using the widget, you can call the API directly.
        Send a message and receive a streaming response:
      </p>
      <Code label="javascript">{`const response = await fetch("https://your-onyx-domain.com/chat/message", {
  method: "POST",
  headers: {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    message: "What are your shipping rates?",
    userId: "customer-123",       // optional
    userName: "Jane Smith",       // optional
    sessionId: "previous-session" // optional, to continue a conversation
  }),
});

// The response is a Server-Sent Events stream
const reader = response.body.getReader();
const decoder = new TextDecoder();
let fullResponse = "";

while (true) {
  const { done, value } = await reader.read();
  if (done) break;

  const chunk = decoder.decode(value);
  for (const line of chunk.split("\\n\\n")) {
    if (!line.startsWith("data: ")) continue;
    const data = JSON.parse(line.slice(6));

    if (data.content) {
      fullResponse += data.content;
      // Update your UI with the new text
    }

    if (data.tool_call) {
      // The AI is fetching data from one of your tools
      // Show a loading indicator like "Looking up your order..."
    }

    if (data.done) {
      // Response complete! data.sessionId is needed for follow-up messages
    }
  }
}`}</Code>
    </section>
  );
}

function TestChatbotSection() {
  return (
    <section className="doc-section" id="test-chatbot">
      <h2>Test Your Chatbot</h2>
      <p>Before going live, use the built-in preview to test your chatbot.</p>

      <ScreenHint
        title="Portal \u2192 Chat Preview"
        description="Talk to your chatbot exactly as your customers would."
      />

      <h3>Things to Test</h3>
      <ul>
        <li><strong>FAQ answers</strong> &mdash; Ask questions from your knowledge base and check the AI gives accurate responses</li>
        <li><strong>Out-of-scope questions</strong> &mdash; Ask something not in your FAQs to see how the AI handles it</li>
        <li><strong>Tool integrations</strong> &mdash; If you've set up tools, try triggering them (e.g. "What's the status of order TEST-001?")</li>
        <li><strong>Transfer to agent</strong> &mdash; Say "I want to talk to a human" and verify the handoff works</li>
        <li><strong>Tone and personality</strong> &mdash; Make sure the AI matches your brand voice</li>
      </ul>

      <InfoBox type="tip" title="Iterate on Your System Prompt">
        <p>If the AI's responses aren't quite right, go to <strong>Settings</strong> and tweak your system prompt. Small changes can make a big difference in tone and accuracy.</p>
      </InfoBox>
    </section>
  );
}

// ─── Tool Integrations ───────────────────────────────────────────────────────

function ToolsIntroSection() {
  return (
    <section className="doc-section" id="tools-intro">
      <h2>What Are Tools?</h2>
      <p>
        Tools let your AI chatbot <strong>connect to your existing systems</strong> to fetch real-time
        information. Instead of giving generic answers, the AI can actually look up orders,
        check account balances, search your product catalog, and more.
      </p>

      <h3>A Real Example</h3>
      <div className="conversation-example">
        <div className="chat-bubble user">What's the status of my order #ORD-5523?</div>
        <div className="chat-tool">
          <span className="tool-badge">{"\u{1F527}"} Looking up order #ORD-5523...</span>
        </div>
        <div className="chat-bubble assistant">
          Your order #ORD-5523 has been <strong>shipped</strong>! It's on its way via UPS with tracking number 1Z999AA10123456784. The estimated delivery date is <strong>February 28th</strong>. Would you like me to help with anything else?
        </div>
      </div>

      <p>
        Without tools, the AI would have to say "I don't have access to order information."
        With tools, it can give customers the exact answer they need.
      </p>

      <h3>How It Works (Behind the Scenes)</h3>
      <div className="flow-visual">
        <div className="flow-step">
          <div className="flow-number">1</div>
          <div className="flow-label">Customer asks about their order</div>
        </div>
        <div className="flow-arrow">{"\u2192"}</div>
        <div className="flow-step">
          <div className="flow-number">2</div>
          <div className="flow-label">AI decides to call your Order API</div>
        </div>
        <div className="flow-arrow">{"\u2192"}</div>
        <div className="flow-step">
          <div className="flow-number">3</div>
          <div className="flow-label">Your API returns order data</div>
        </div>
        <div className="flow-arrow">{"\u2192"}</div>
        <div className="flow-step">
          <div className="flow-number">4</div>
          <div className="flow-label">AI crafts a friendly response with the data</div>
        </div>
      </div>

      <InfoBox type="note" title="Your Data Stays Yours">
        <p>Onyx AI calls your API on-demand and does not store the response. The AI only uses the data to answer the current question.</p>
      </InfoBox>

      <h3>Plan Availability</h3>
      <table className="param-table">
        <thead>
          <tr><th>Plan</th><th>Tools Available</th></tr>
        </thead>
        <tbody>
          <tr><td>Free</td><td>Not available</td></tr>
          <tr><td>Starter</td><td>Up to 3 tools</td></tr>
          <tr><td>Pro</td><td>Up to 10 tools</td></tr>
          <tr><td>Business</td><td>Unlimited</td></tr>
        </tbody>
      </table>
    </section>
  );
}

function ToolsSetupSection() {
  return (
    <section className="doc-section" id="tools-setup">
      <h2>Setting Up a Tool</h2>

      <ScreenHint
        title="Portal \u2192 Tools \u2192 Add Tool"
        description="Connect one of your APIs so the AI can fetch real-time data."
      />

      <StepList>
        <li>
          <strong>Go to Tools</strong>
          <p>In the portal sidebar, click <strong>Tools</strong>, then <strong>Add Tool</strong>.</p>
        </li>
        <li>
          <strong>Give it a name</strong>
          <p>Choose a descriptive function name using lowercase and underscores. This is how the AI identifies the tool internally.</p>
          <div className="example-row">
            <span className="example-good">{"\u2713"} lookup_order</span>
            <span className="example-good">{"\u2713"} check_balance</span>
            <span className="example-bad">{"\u2717"} My Order Tool</span>
            <span className="example-bad">{"\u2717"} LookupOrder</span>
          </div>
        </li>
        <li>
          <strong>Write a clear description</strong>
          <p>This is the most important field. The AI reads this description to decide <em>when</em> to use the tool. Be specific about what it does and when it should be used.</p>
          <div className="example-row">
            <span className="example-good">{"\u2713"} &quot;Look up a customer order by order ID. Use when a customer asks about order status, shipping, tracking, or delivery."</span>
          </div>
          <div className="example-row">
            <span className="example-bad">{"\u2717"} &quot;Order tool&quot; (too vague &mdash; AI won't know when to use it)</span>
          </div>
        </li>
        <li>
          <strong>Set the API endpoint</strong>
          <p>Enter the URL of your API. Use <code>{`{parameter_name}`}</code> for dynamic values in the URL path.</p>
          <Code>{`https://api.yourstore.com/orders/{order_id}`}</Code>
          <p>When the AI calls this tool with order_id = "ORD-5523", it becomes:</p>
          <Code>{`https://api.yourstore.com/orders/ORD-5523`}</Code>
        </li>
        <li>
          <strong>Choose the HTTP method</strong>
          <p><strong>GET</strong> for fetching data (most common). <strong>POST</strong> for sending data (e.g. search queries).</p>
        </li>
        <li>
          <strong>Add authentication headers (if needed)</strong>
          <p>If your API requires an API key or token, add it in the headers section:</p>
          <Code label="json">{`{
  "Authorization": "Bearer your-internal-api-token",
  "X-API-Key": "your-api-key"
}`}</Code>
        </li>
        <li>
          <strong>Define parameters</strong>
          <p>Tell the AI what information it needs to collect from the customer before calling the tool. See <a href="#tools-examples">examples</a> below.</p>
        </li>
        <li>
          <strong>Set up response mapping (optional)</strong>
          <p>If your API returns a lot of data, you can filter it down to just the fields the AI needs. This keeps responses focused and avoids leaking internal data.</p>
          <Code label="json">{`{
  "status": "data.order.status",
  "tracking": "data.order.tracking_number",
  "eta": "data.order.estimated_delivery"
}`}</Code>
          <p>The AI will only see <code>status</code>, <code>tracking</code>, and <code>eta</code> &mdash; not your internal pricing, margins, or other sensitive fields.</p>
        </li>
      </StepList>

      <InfoBox type="warning" title="Keep Sensitive Data Out">
        <p>Use response mapping to avoid exposing internal costs, employee info, or other sensitive data to the AI. Only map the fields that are relevant to customer-facing answers.</p>
      </InfoBox>
    </section>
  );
}

function ToolsExamplesSection() {
  return (
    <section className="doc-section" id="tools-examples">
      <h2>Common Tool Examples</h2>
      <p>Here are ready-to-use configurations for common use cases. Adapt them to match your API.</p>

      <h3>Order Status Lookup</h3>
      <p>Let customers ask about their order and get real-time status.</p>
      <div className="tool-config">
        <table className="param-table">
          <tbody>
            <tr><td>Name</td><td><code>lookup_order</code></td></tr>
            <tr><td>Display Name</td><td>Order Lookup</td></tr>
            <tr><td>Description</td><td>"Look up a customer order by order ID or order number. Use when a customer asks about their order status, shipping, tracking, or delivery."</td></tr>
            <tr><td>Endpoint</td><td><code>{`https://api.yourstore.com/orders/{order_id}`}</code></td></tr>
            <tr><td>Method</td><td>GET</td></tr>
          </tbody>
        </table>
        <h4>Parameters</h4>
        <Code label="json">{`{
  "type": "object",
  "properties": {
    "order_id": {
      "type": "string",
      "description": "The order ID or order number"
    }
  },
  "required": ["order_id"]
}`}</Code>
        <h4>Response Mapping</h4>
        <Code label="json">{`{
  "status": "data.status",
  "tracking_number": "data.tracking.number",
  "estimated_delivery": "data.tracking.eta",
  "items": "data.items"
}`}</Code>
      </div>

      <h3>Product Search</h3>
      <p>Let the AI search your product catalog when customers ask about products or need recommendations.</p>
      <div className="tool-config">
        <table className="param-table">
          <tbody>
            <tr><td>Name</td><td><code>search_products</code></td></tr>
            <tr><td>Display Name</td><td>Product Search</td></tr>
            <tr><td>Description</td><td>"Search the product catalog by keyword, category, or price range. Use when a customer asks about products, availability, pricing, or wants recommendations."</td></tr>
            <tr><td>Endpoint</td><td><code>https://api.yourstore.com/products/search</code></td></tr>
            <tr><td>Method</td><td>POST</td></tr>
          </tbody>
        </table>
        <h4>Parameters</h4>
        <Code label="json">{`{
  "type": "object",
  "properties": {
    "query": {
      "type": "string",
      "description": "Search keyword or product name"
    },
    "category": {
      "type": "string",
      "description": "Product category to filter by",
      "enum": ["electronics", "clothing", "home", "sports"]
    },
    "max_price": {
      "type": "number",
      "description": "Maximum price filter in dollars"
    }
  },
  "required": ["query"]
}`}</Code>
      </div>

      <h3>Account Balance</h3>
      <p>Let customers check their account or subscription status.</p>
      <div className="tool-config">
        <table className="param-table">
          <tbody>
            <tr><td>Name</td><td><code>check_balance</code></td></tr>
            <tr><td>Display Name</td><td>Account Balance</td></tr>
            <tr><td>Description</td><td>"Check a customer's account balance, credits, or subscription status. Use when a customer asks about billing, balance, payments, or their plan."</td></tr>
            <tr><td>Endpoint</td><td><code>{`https://api.yourservice.com/accounts/{account_id}/balance`}</code></td></tr>
            <tr><td>Method</td><td>GET</td></tr>
          </tbody>
        </table>
        <h4>Parameters</h4>
        <Code label="json">{`{
  "type": "object",
  "properties": {
    "account_id": {
      "type": "string",
      "description": "The customer's account ID or email address"
    }
  },
  "required": ["account_id"]
}`}</Code>
        <h4>Response Mapping</h4>
        <Code label="json">{`{
  "balance": "data.current_balance",
  "currency": "data.currency",
  "plan": "data.subscription.plan_name",
  "next_billing": "data.subscription.next_billing_date"
}`}</Code>
      </div>

      <InfoBox type="tip" title="The AI Is Smart About Parameters">
        <p>You don't need to ask the customer for the exact parameter. If a customer says "What's happening with order five five two three?", the AI will extract "5523" and pass it as the <code>order_id</code>. It handles natural language gracefully.</p>
      </InfoBox>
    </section>
  );
}

function ToolsTestingSection() {
  return (
    <section className="doc-section" id="tools-testing">
      <h2>Testing Your Tools</h2>
      <p>Always test your tools before relying on them in production.</p>

      <ScreenHint
        title="Portal \u2192 Tools \u2192 (select a tool) \u2192 Test"
        description="Send a test request to your API and see the response."
      />

      <h3>Built-in Test Feature</h3>
      <StepList>
        <li>
          <strong>Open the tool</strong> you want to test from the Tools page</li>
        <li>
          <strong>Click "Test"</strong> and enter sample parameter values (e.g. a real order ID)</li>
        <li>
          <strong>Review the response</strong> &mdash; you'll see the HTTP status, latency, and response data</li>
      </StepList>

      <h3>What to Check</h3>
      <ul>
        <li><strong>Status 200</strong> &mdash; The API responded successfully</li>
        <li><strong>Latency under 5 seconds</strong> &mdash; Slow APIs make the chatbot feel sluggish</li>
        <li><strong>Response data</strong> &mdash; Verify the data looks correct and your response mapping extracts the right fields</li>
      </ul>

      <h3>End-to-End Test</h3>
      <p>After the built-in test passes, go to <strong>Chat Preview</strong> and ask a question that should trigger the tool. For example:</p>
      <div className="conversation-example">
        <div className="chat-bubble user">What's the status of order TEST-001?</div>
        <div className="chat-tool">
          <span className="tool-badge">{"\u{1F527}"} Calling lookup_order...</span>
        </div>
        <div className="chat-bubble assistant">Your order TEST-001 is currently being processed and should ship within 24 hours. Is there anything else I can help with?</div>
      </div>

      <InfoBox type="warning" title="Common Issues">
        <ul>
          <li><strong>Tool never gets called?</strong> &mdash; Improve the tool's description so the AI knows when to use it</li>
          <li><strong>Authentication error (401/403)?</strong> &mdash; Double-check your API key in the headers</li>
          <li><strong>Timeout?</strong> &mdash; Your API might be slow; try increasing the timeout (max 30 seconds)</li>
          <li><strong>Wrong data extracted?</strong> &mdash; Check your response mapping dot-paths match the actual API response structure</li>
        </ul>
      </InfoBox>
    </section>
  );
}

// ─── Live Agent Support ──────────────────────────────────────────────────────

function AgentsOverviewSection() {
  return (
    <section className="doc-section" id="agents-overview">
      <h2>How Live Chat Works</h2>
      <p>
        When the AI chatbot can't fully resolve a customer's issue, it can hand the conversation
        off to a real person on your team. This happens seamlessly within the same chat window.
      </p>

      <h3>When Does a Transfer Happen?</h3>
      <p>The system automatically detects when a customer might need human help:</p>
      <ul>
        <li><strong>Customer asks directly</strong> &mdash; "Can I talk to a real person?"</li>
        <li><strong>Frustration detected</strong> &mdash; Keywords like "this is useless" or "I'm frustrated"</li>
        <li><strong>Complex issues</strong> &mdash; Billing disputes, refund requests, complaints</li>
        <li><strong>Urgent situations</strong> &mdash; Words like "urgent", "emergency", "ASAP"</li>
        <li><strong>AI struggling</strong> &mdash; Multiple unhelpful responses in a row</li>
      </ul>

      <h3>The Customer Experience</h3>
      <div className="conversation-example">
        <div className="chat-bubble user">I've been charged twice for the same order and I need this fixed NOW.</div>
        <div className="chat-bubble assistant">I understand how frustrating a double charge must be. I'd like to connect you with a billing specialist who can resolve this immediately. Would you like me to transfer you?</div>
        <div className="chat-system">{"\u{1F504}"} Connecting you with Sarah from our support team...</div>
        <div className="chat-bubble agent">Hi! I'm Sarah. I can see your conversation history and I'm pulling up your account now. Let me get this sorted for you right away.</div>
      </div>

      <InfoBox type="note" title="Queue System">
        <p>If all agents are busy, the customer is placed in a queue and sees their position and estimated wait time. When an agent becomes available, the next customer in line is automatically connected.</p>
      </InfoBox>
    </section>
  );
}

function AgentsManageSection() {
  return (
    <section className="doc-section" id="agents-manage">
      <h2>Managing Your Team</h2>

      <ScreenHint
        title="Portal \u2192 Agents"
        description="Add team members, set roles, and manage agent accounts."
      />

      <h3>Adding an Agent</h3>
      <StepList>
        <li><strong>Click "Add Agent"</strong> on the Agents page</li>
        <li><strong>Enter their details</strong> &mdash; name, email, and a temporary password</li>
        <li><strong>Set their display name</strong> &mdash; this is what customers see (e.g. "Sarah")</li>
        <li><strong>Set max concurrent chats</strong> &mdash; how many conversations they can handle at once (default: 3)</li>
        <li><strong>Share their login</strong> &mdash; the agent can log in to the portal with their credentials</li>
      </StepList>

      <h3>Agent Statuses</h3>
      <table className="param-table">
        <thead>
          <tr><th>Status</th><th>Meaning</th><th>Receives New Chats?</th></tr>
        </thead>
        <tbody>
          <tr><td><span className="status-dot online"></span> Online</td><td>Available and ready</td><td>Yes</td></tr>
          <tr><td><span className="status-dot busy"></span> Busy</td><td>Working but at capacity</td><td>No</td></tr>
          <tr><td><span className="status-dot break"></span> On Break</td><td>Temporarily away</td><td>No</td></tr>
          <tr><td><span className="status-dot offline"></span> Offline</td><td>Not working</td><td>No</td></tr>
        </tbody>
      </table>

      <h3>Shift Scheduling</h3>
      <p>
        You can set weekly shift schedules for each agent. This helps with planning but doesn't
        prevent agents from clocking in outside their scheduled hours.
      </p>
    </section>
  );
}

function AgentWorkspaceSection() {
  return (
    <section className="doc-section" id="agents-workspace">
      <h2>Agent Workspace</h2>
      <p>
        When an agent logs in, they see their dedicated workspace with all active conversations.
      </p>

      <ScreenHint
        title="Portal \u2192 Agent Workspace (agent view)"
        description="Real-time chat interface with customer context and tools."
      />

      <h3>Workspace Features</h3>
      <ul>
        <li><strong>Active Chats</strong> &mdash; List of all assigned conversations with unread indicators</li>
        <li><strong>Full History</strong> &mdash; See the entire AI conversation before the transfer</li>
        <li><strong>Customer Context</strong> &mdash; View customer name, user type, and metadata</li>
        <li><strong>Internal Notes</strong> &mdash; Add private notes (not visible to the customer)</li>
        <li><strong>Canned Responses</strong> &mdash; Quick-insert pre-written replies</li>
        <li><strong>AI Suggestions</strong> &mdash; Get AI-generated response suggestions</li>
        <li><strong>Transfer</strong> &mdash; Hand off to another agent if needed</li>
        <li><strong>Resolve</strong> &mdash; Mark the conversation as complete</li>
        <li><strong>Clock In/Out</strong> &mdash; Track shift hours</li>
      </ul>

      <InfoBox type="tip" title="AI Suggestions">
        <p>Agents can click the AI suggestion button to get a recommended response based on the conversation context. This helps agents reply faster while maintaining quality.</p>
      </InfoBox>
    </section>
  );
}

function AgentNotificationsSection() {
  return (
    <section className="doc-section" id="agent-notifications">
      <h2>Notifications &amp; Alerts</h2>
      <p>
        Never miss a customer message. The agent workspace includes built-in notification
        support to keep your team responsive even when they're multitasking.
      </p>

      <h3>Notification Types</h3>
      <ul>
        <li><strong>Sound Alerts</strong> &mdash; A distinct tone plays when a new chat is assigned (higher pitch) or when a customer sends a message (softer nudge). Toggle on/off via the speaker icon in the workspace header.</li>
        <li><strong>Desktop Notifications</strong> &mdash; Native browser notifications appear even when the Onyx tab is in the background. Click the bell icon to enable, and grant browser permission when prompted.</li>
        <li><strong>Tab Title Flashing</strong> &mdash; When new messages arrive while the tab is inactive, the browser tab title flashes with an unread count (e.g., "(3) New Message"). This clears automatically when you return to the tab.</li>
      </ul>

      <h3>Notification Controls</h3>
      <p>
        In the agent workspace sidebar, you'll find two toggle buttons next to the Clock In/Out button:
      </p>
      <ul>
        <li><strong>Speaker icon</strong> &mdash; Toggle sound alerts on/off</li>
        <li><strong>Bell icon</strong> &mdash; Toggle desktop notifications on/off (first click requests browser permission)</li>
      </ul>

      <InfoBox type="tip" title="Preferences Are Saved">
        <p>Your notification preferences are saved in your browser's local storage, so they persist across sessions. Each agent can customize their own settings independently.</p>
      </InfoBox>
    </section>
  );
}

function AgentWidgetSection() {
  return (
    <section className="doc-section" id="agent-widget">
      <h2>Embeddable Agent Widget</h2>
      <p>
        The Agent Widget lets your CS team respond to customer chats from <strong>any website or internal system</strong> &mdash;
        no need to open the Onyx portal. Simply embed a lightweight script tag into your CRM, helpdesk, or internal dashboard.
      </p>

      <InfoBox type="note" title="Available on Pro &amp; Business Plans">
        <p>The embeddable agent widget is available on Pro and Business plans. Upgrade from the billing page if needed.</p>
      </InfoBox>

      <h3>How It Works</h3>
      <ul>
        <li>A floating chat console appears in the corner of any page where you embed the script</li>
        <li>Agents authenticate using their JWT token (obtained from the portal login)</li>
        <li>The widget connects to the same WebSocket backend &mdash; no separate setup needed</li>
        <li>Agents can view assigned chats, send messages, resolve conversations, and change their status</li>
        <li>Sound notifications are built-in to alert agents of new chats and messages</li>
      </ul>

      <h3>Embed Code</h3>
      <p>Add this script tag to any HTML page where agents should have access:</p>
      <Code label="HTML">{`<script
  src="https://onyxai.chat/agent-widget.js"
  data-agent-token="YOUR_AGENT_JWT_TOKEN"
  data-server-url="https://onyxai.chat"
  data-position="bottom-right"
  data-theme="dark">
</script>`}</Code>

      <h3>Configuration Options</h3>
      <ul>
        <li><strong>data-agent-token</strong> (required) &mdash; The agent's JWT authentication token from portal login</li>
        <li><strong>data-server-url</strong> (required) &mdash; Your Onyx server URL</li>
        <li><strong>data-position</strong> (optional) &mdash; <code>bottom-right</code> (default) or <code>bottom-left</code></li>
        <li><strong>data-theme</strong> (optional) &mdash; <code>dark</code> (default) or <code>light</code></li>
      </ul>

      <h3>Getting the Agent Token</h3>
      <p>
        Agents can find their embed code in the portal under <strong>Credentials</strong> &rarr; <strong>Agent Widget</strong>.
        The token is a JWT that authenticates the agent's WebSocket connection. For security:
      </p>
      <ul>
        <li>Each agent has their own unique token</li>
        <li>Tokens are tied to the agent's account and permissions</li>
        <li>Never share tokens publicly or commit them to version control</li>
      </ul>

      <InfoBox type="warning" title="Token Security">
        <p>Agent tokens provide full access to respond to customer conversations. Treat them like passwords &mdash; only embed them in trusted internal systems, not public-facing websites.</p>
      </InfoBox>

      <h3>Widget Features</h3>
      <ul>
        <li>View and switch between active chats</li>
        <li>Send messages and resolve conversations</li>
        <li>Change agent status (Online, Busy, Break, Offline)</li>
        <li>Sound alerts for new chats and messages (with mute toggle)</li>
        <li>Unread count badge on the widget button</li>
        <li>Auto-reconnect on connection loss</li>
        <li>Dark and light theme support</li>
      </ul>
    </section>
  );
}

function BusinessHoursSection() {
  return (
    <section className="doc-section" id="business-hours">
      <h2>Business Hours</h2>
      <p>
        Set your support team's availability schedule. When enabled, live agent transfers are
        only offered during business hours.
      </p>

      <ScreenHint
        title="Portal \u2192 Business Hours"
        description="Configure your weekly availability schedule."
      />

      <h3>How It Works</h3>
      <ul>
        <li><strong>During business hours</strong> &mdash; Customers can be transferred to live agents as normal</li>
        <li><strong>Outside business hours</strong> &mdash; The AI continues to help, but offers an offline message form instead of agent transfer</li>
        <li><strong>AI is always available</strong> &mdash; Business hours only affect live agent availability, not the AI chatbot itself</li>
      </ul>

      <InfoBox type="note" title="Timezone">
        <p>Business hours use the timezone configured in your account settings. Make sure this matches your team's working timezone.</p>
      </InfoBox>
    </section>
  );
}

function CannedResponsesSection() {
  return (
    <section className="doc-section" id="canned-responses">
      <h2>Canned Responses</h2>
      <p>
        Create pre-written responses that agents can quickly insert during conversations.
        Great for common replies like greetings, refund confirmations, or escalation messages.
      </p>

      <ScreenHint
        title="Portal \u2192 Canned Responses"
        description="Create and manage quick-reply templates for your agents."
      />

      <h3>Example Canned Responses</h3>
      <div className="canned-example">
        <div className="canned-title">Greeting</div>
        <div className="canned-text">Hi there! Thanks for reaching out. I've reviewed your conversation with our AI assistant. How can I help you further?</div>
      </div>
      <div className="canned-example">
        <div className="canned-title">Refund Initiated</div>
        <div className="canned-text">I've initiated a refund for your order. You should see the amount back in your account within 5-7 business days. Is there anything else I can help with?</div>
      </div>
      <div className="canned-example">
        <div className="canned-title">Escalation</div>
        <div className="canned-text">I'm going to transfer you to a senior specialist who can better assist with this. They'll have full context of our conversation. One moment please.</div>
      </div>
    </section>
  );
}

// ─── Account ─────────────────────────────────────────────────────────────────

function PlansSection() {
  return (
    <section className="doc-section" id="plans">
      <h2>Plans & Billing</h2>

      <ScreenHint
        title="Portal \u2192 Billing"
        description="View your current plan, usage, and upgrade options."
      />

      <div className="tier-grid">
        <div className="tier-card">
          <div className="tier-name">Free</div>
          <div className="tier-price">$0<span>/mo</span></div>
          <ul className="tier-features">
            <li>100 messages/month</li>
            <li>1 live agent</li>
            <li>Basic analytics</li>
            <li>No API integrations</li>
          </ul>
        </div>
        <div className="tier-card">
          <div className="tier-name">Starter</div>
          <div className="tier-price">$19.99<span>/mo</span></div>
          <ul className="tier-features">
            <li>5,000 messages/month</li>
            <li>Up to 3 agents</li>
            <li>Custom branding</li>
            <li>Up to 3 API tools</li>
          </ul>
        </div>
        <div className="tier-card highlighted">
          <div className="tier-name">Pro</div>
          <div className="tier-price">$49.99<span>/mo</span></div>
          <ul className="tier-features">
            <li>20,000 messages/month</li>
            <li>Up to 10 agents</li>
            <li>Advanced analytics</li>
            <li>Business hours</li>
            <li>Up to 10 API tools</li>
          </ul>
        </div>
        <div className="tier-card">
          <div className="tier-name">Business</div>
          <div className="tier-price">$99.99<span>/mo</span></div>
          <ul className="tier-features">
            <li>50,000 messages/month</li>
            <li>Unlimited agents</li>
            <li>Unlimited tools</li>
            <li>Dedicated support</li>
          </ul>
        </div>
      </div>

      <h3>Usage Tracking</h3>
      <p>
        Each message from a customer counts toward your monthly limit. AI responses, tool calls,
        and agent messages do <strong>not</strong> count &mdash; only customer messages do.
      </p>
      <p>
        When you're approaching your limit, you'll see a warning in the dashboard. All paid
        plans include a 10% grace buffer so your chatbot doesn't suddenly stop mid-conversation.
      </p>
    </section>
  );
}

function CredentialsSection() {
  return (
    <section className="doc-section" id="api-credentials">
      <h2>API Credentials</h2>

      <ScreenHint
        title="Portal \u2192 Credentials"
        description="Your API key and embed code."
      />

      <h3>Your API Key</h3>
      <p>
        Your API key is the unique identifier that connects your website's chat widget to your
        Onyx AI account. It's generated automatically when you create your account.
      </p>

      <InfoBox type="danger" title="Keep Your API Key Secret">
        <p>Your API key gives access to your chatbot. Don't share it publicly, commit it to version control, or expose it in client-side code beyond the chat widget embed.</p>
      </InfoBox>

      <h3>Regenerating Your Key</h3>
      <p>
        If your API key is compromised, you can regenerate it from the Credentials page.
        Note that this will immediately invalidate the old key &mdash; you'll need to update
        the embed code on your website.
      </p>
    </section>
  );
}

function TroubleshootingSection() {
  return (
    <section className="doc-section" id="troubleshooting">
      <h2>Troubleshooting</h2>

      <h3>Common Issues</h3>

      <div className="faq-example">
        <div className="faq-q">The chatbot gives wrong or generic answers</div>
        <div className="faq-a">
          <ul>
            <li>Add more FAQs covering common questions</li>
            <li>Make your FAQ answers more detailed and specific</li>
            <li>Improve your system prompt with clearer instructions</li>
            <li>If using tools, check that the tool descriptions clearly explain when to use them</li>
          </ul>
        </div>
      </div>

      <div className="faq-example">
        <div className="faq-q">Tools aren't being called by the AI</div>
        <div className="faq-a">
          <ul>
            <li>Make sure the tool is <strong>active</strong> (toggle it on in the Tools page)</li>
            <li>Improve the tool's <strong>description</strong> &mdash; it should clearly state when to use it</li>
            <li>Check that your plan supports tool integrations (Starter or above)</li>
            <li>Test the tool endpoint directly to make sure it's reachable</li>
          </ul>
        </div>
      </div>

      <div className="faq-example">
        <div className="faq-q">Tool returns an error</div>
        <div className="faq-a">
          <ul>
            <li>Use the <strong>Test</strong> button on the tool page to see the exact error</li>
            <li>Check authentication headers are correct</li>
            <li>Verify the endpoint URL and parameter placeholders</li>
            <li>Make sure your API is accessible from the internet (not localhost)</li>
            <li>Try increasing the timeout if your API is slow</li>
          </ul>
        </div>
      </div>

      <div className="faq-example">
        <div className="faq-q">Live agent transfer isn't working</div>
        <div className="faq-a">
          <ul>
            <li>Make sure at least one agent is <strong>online</strong></li>
            <li>Check that business hours are configured correctly (or disabled if you want 24/7 agent availability)</li>
            <li>Verify the agent's <strong>max concurrent chats</strong> isn't reached</li>
          </ul>
        </div>
      </div>

      <div className="faq-example">
        <div className="faq-q">Chat widget doesn't appear on my website</div>
        <div className="faq-a">
          <ul>
            <li>Verify your API key is correct in the embed code</li>
            <li>Check the browser console for JavaScript errors</li>
            <li>Make sure the script tag is placed before <code>&lt;/body&gt;</code></li>
            <li>Ensure your domain isn't blocking the script with Content Security Policy</li>
          </ul>
        </div>
      </div>

      <InfoBox type="note" title="Need More Help?">
        <p>If you're stuck, reach out to our support team. Pro and Business plan customers get priority support.</p>
      </InfoBox>
    </section>
  );
}

// ─── Main App ────────────────────────────────────────────────────────────────

function DocsApp() {
  const [activeSection, setActiveSection] = useState("welcome");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);

      const sections = document.querySelectorAll(".doc-section");
      for (const section of Array.from(sections).reverse()) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 120) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="docs-layout">
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? "\u2715" : "\u2630"}
      </button>

      <div className={`docs-sidebar ${sidebarOpen ? "open" : ""}`}>
        <Sidebar
          activeSection={activeSection}
          onClose={() => setSidebarOpen(false)}
        />
      </div>

      <main className="docs-main">
        <header className="docs-header">
          <span className="docs-header-title">Onyx AI {"\u00B7"} User Guide</span>
          <div className="docs-header-links">
            <a href="/">Home</a>
            <a href="/docs/api">API Reference</a>
            <a href="/portal">Go to Portal</a>
          </div>
        </header>

        <div className="docs-content">
          <WelcomeSection />
          <CreateAccountSection />
          <DashboardSection />
          <BrandingSection />
          <KnowledgeBaseSection />
          <EmbedSection />
          <TestChatbotSection />
          <ToolsIntroSection />
          <ToolsSetupSection />
          <ToolsExamplesSection />
          <ToolsTestingSection />
          <AgentsOverviewSection />
          <AgentsManageSection />
          <AgentWorkspaceSection />
          <AgentNotificationsSection />
          <AgentWidgetSection />
          <BusinessHoursSection />
          <CannedResponsesSection />
          <PlansSection />
          <CredentialsSection />
          <TroubleshootingSection />
        </div>
      </main>

      <button
        className={`back-to-top ${showBackToTop ? "visible" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        &#8593;
      </button>
    </div>
  );
}

const root = createRoot(document.getElementById("root")!);
root.render(<DocsApp />);
